import React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import {
    useGetStudentDashboardQuery
} from "@/APIConnect";
import StudentSidebar from '@/components/student-dashboard/components/StudentSidebar';
/* Select components removed – dropdown not needed */
import '../../Dashboard/StudentDashboardV2.css';
import '../InnovationPod.css';
import ProblemStatementModal from './ProblemStatementModal';

interface PodLayoutProps {
    podData?: any;
    activeTab?: 'overview' | 'workspace' | 'team_room' | 'files';
    children: React.ReactNode;
    isEmpty?: boolean;
    headerContent?: React.ReactNode;
}

const extractId = (id: any): string => {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object') {
        const { timestamp, machine, pid, increment } = id;
        if (timestamp !== undefined && machine !== undefined && pid !== undefined && increment !== undefined) {
            const h = (n: number, len: number) => (n >>> 0).toString(16).padStart(len, '0').slice(-len);
            return `${h(timestamp, 8)}${h(machine, 6)}${h(pid, 4)}${h(increment, 6)}`.toLowerCase();
        }
    }
    return String(id);
};

export const PodLayout: React.FC<PodLayoutProps> = ({ podData, activeTab, children, isEmpty }) => {
    const { user } = useAuth0();
    const userId = user?.sub?.replace('auth0|', '') || '';
    const navigate = useNavigate();
    const location = useLocation();
    const [isProblemModalOpen, setIsProblemModalOpen] = React.useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const selectedCourseId = searchParams.get('courseId');

    const { data: dashboardResponse } = useGetStudentDashboardQuery(userId, { skip: !userId });
    const studentCourses = dashboardResponse?.resultObject?.courses || dashboardResponse?.courses || [];

    const podId = extractId(podData?.id);

    const handleCourseChange = (courseId: string) => {
        const course = studentCourses.find((c: any) => (c.courseId || c.id) === courseId);
        if (!course) return;
        const classroomId = course.classroomId || course.ClassroomId;
        setSearchParams({ courseId, classroomId });
    };

    // Auto-select first course if none selected
    React.useEffect(() => {
        if (studentCourses.length > 0 && !selectedCourseId) {
            handleCourseChange(studentCourses[0].courseId || studentCourses[0].id);
        }
    }, [studentCourses, selectedCourseId]);

    const handleTabClick = (tabId: string) => {
        const search = location.search;
        if (tabId === 'ov') navigate(`/innovation-pod${search}`);
        if (tabId === 'ws') navigate(`/innovation-pod/workspace${search}`);
        if (tabId === 'tr') navigate(podId ? `/innovation-pod/pod-room/${podId}${search}` : `/innovation-pod/pod-room${search}`);
        if (tabId === 'fi') navigate(podId ? `/innovation-pod/pod-files/${podId}${search}` : `/innovation-pod/pod-files${search}`);
    };

    const durationWeeks = (podData?.startDate && podData?.endDate) ? Math.max(1, Math.round((new Date(podData.endDate).getTime() - new Date(podData.startDate).getTime()) / (1000 * 60 * 60 * 24 * 7))) : undefined;

    return (
        <div className="dashboard-v2-body innovation-pod-page">
            <div className="shell">
                {/* ── SIDEBAR ── */}
                <StudentSidebar />

                <div className="content" style={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 0, overflowY: 'auto', overflowX: 'hidden', borderLeft: '4px solid rgba(0,0,0,0.12)' }}>
                    {/* Integrated Course Selector Header - Green Background */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        // justifyContent removed to collapse space when dropdown is gone
                        padding: '14px 24px',
                        background: '#346c6a',
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 20,
                        gap: '16px',
                        flexWrap: 'wrap' as const,
                        flexShrink: 0,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                padding: '8px',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                                </svg>
                            </div>
                            <div>
                                <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#fff', lineHeight: 1.3, margin: 0 }}>Innovation Pod</h2>
                                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', fontWeight: 500, margin: 0 }}>Your collaborative innovation journey</p>
                            </div>
                        </div>

                    </div>
                    {isEmpty ? (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                            <div style={{ textAlign: 'center', padding: '60px 40px', background: 'var(--surface)', borderRadius: '16px', border: '1px solid var(--border)', maxWidth: '440px', width: '100%', boxShadow: 'var(--shadow-sm)' }}>
                                <svg width="56" height="56" fill="none" stroke="var(--text-4)" strokeWidth="1.2" style={{ margin: '0 auto 20px' }}>
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M12 8v4" /><path d="M12 16h.01" />
                                </svg>
                                <h3 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text)', marginBottom: '10px' }}>No Active Innovation Pod</h3>
                                <p style={{ fontSize: '14px', color: 'var(--text-3)', lineHeight: 1.6 }}>You don't have any active Innovation Pod linked to your account at this time.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="innovation-pod-wrapper flex-1 min-w-0" style={{ padding: '0px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div className="pod-hero anim a1" style={{ padding: '6px 6px 0' }}>
                                <div className="pod-info-tile">
                                    <svg className="pod-illo" width="180" height="180" viewBox="0 0 180 180" fill="none" style={{ position: 'absolute', right: 10, bottom: -10, opacity: .08, zIndex: 0 }}>
                                        <circle cx="90" cy="60" r="40" stroke="#fff" strokeWidth="2" />
                                        <path d="M75 95c0-12 5-18 15-24s15-12 15-24" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 4" />
                                        <path d="M105 95c0-12-5-18-15-24" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 4" />
                                        <path d="M82 45c4 10 8 5 8 15s4 5 8 15" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                                        <rect x="78" y="96" width="24" height="6" rx="2" stroke="#fff" strokeWidth="1.5" />
                                        <rect x="80" y="104" width="20" height="4" rx="2" stroke="#fff" strokeWidth="1.2" />
                                        <rect x="83" y="110" width="14" height="3" rx="1.5" stroke="#fff" strokeWidth="1" />
                                        <path d="M90 10v10" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M50 25l7 7" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                                        <path d="M130 25l-7 7" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                                        <path d="M30 60h10" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                                        <path d="M140 60h10" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                                        <circle cx="145" cy="130" r="15" stroke="#fff" strokeWidth="1" strokeDasharray="4 3" />
                                        <circle cx="145" cy="130" r="5" stroke="#fff" strokeWidth="1" />
                                        <circle cx="125" cy="148" r="10" stroke="#fff" strokeWidth=".8" strokeDasharray="3 3" />
                                        <circle cx="125" cy="148" r="3.5" stroke="#fff" strokeWidth=".8" />
                                        <circle cx="40" cy="135" r="3" fill="rgba(255,255,255,.4)" />
                                        <circle cx="55" cy="125" r="2" fill="rgba(255,255,255,.3)" />
                                        <circle cx="48" cy="150" r="2.5" fill="rgba(255,255,255,.3)" />
                                        <line x1="40" y1="135" x2="55" y2="125" stroke="#fff" strokeWidth=".6" opacity=".3" />
                                        <line x1="40" y1="135" x2="48" y2="150" stroke="#fff" strokeWidth=".6" opacity=".3" />
                                        <line x1="55" y1="125" x2="48" y2="150" stroke="#fff" strokeWidth=".6" opacity=".25" />
                                    </svg>
                                    <div className="pod-hero-inner">
                                        <div className="pod-eyebrow">Innovation Pod</div>
                                        <div className="pod-title">{podData?.podTitle || 'Untitled Pod'}</div>
                                        <div className="pod-desc line-clamp-3">{podData?.problemStatement || 'Loading pod description...'}</div>
                                        <button className="prob-view-full" style={{ color: 'rgba(168, 240, 208, 0.9)' }} onClick={() => setIsProblemModalOpen(true)}>
                                            View full
                                        </button>
                                        <div className="pod-meta">
                                            <div className="pod-m"><div className="pod-mv" style={{ color: 'var(--teal)' }}>Active</div><div className="pod-ml">Status</div></div>
                                            <div className="pod-m"><div className="pod-mv">{podData?.podType || 'N/A'}</div><div className="pod-ml">Type</div></div>
                                            <div className="pod-m"><div className="pod-mv">{podData?.podCategory || 'N/A'}</div><div className="pod-ml">Category</div></div>
                                            <div className="pod-m"><div className="pod-mv">
                                                {podData?.startDate && podData?.endDate ? `${new Date(podData.startDate).toLocaleString('default', { month: 'short' })}–${new Date(podData.endDate).toLocaleString('default', { month: 'short', year: '2-digit' })}` : 'N/A'}
                                            </div><div className="pod-ml">Timeline</div></div>
                                            <div className="pod-m"><div className="pod-mv">{durationWeeks ? `${durationWeeks} Weeks` : 'N/A'}</div><div className="pod-ml">Duration</div></div>
                                            <div className="pod-m"><div className="pod-mv">{podData?.podMembership?.studentIds?.length || 0}</div><div className="pod-ml">Members</div></div>
                                        </div>
                                    </div>
                                </div>
                                <div className="pod-video-tile">
                                    <div className="pod-video-overlay">
                                        <div className="pod-video-play"><svg width="16" height="16" fill="#fff"><path d="M5 3v10l8.5-5z" /></svg></div>
                                        <div className="pod-video-lbl">What is an Innovation Pod?</div>
                                    </div>
                                    <div className="pod-video-dur">2:45</div>
                                </div>
                            </div>

                            <div className="pod-body" style={{ padding: '12px 6px 20px' }}>
                                <div className="pod-tabs anim a2">
                                    <button className={`pod-tab ${activeTab === 'overview' ? 'on' : ''}`} onClick={() => handleTabClick('ov')}>Overview</button>
                                    <button className={`pod-tab ${activeTab === 'workspace' ? 'on' : ''}`} onClick={() => handleTabClick('ws')}>Workspace</button>
                                    <button className={`pod-tab ${activeTab === 'team_room' ? 'on' : ''}`} onClick={() => handleTabClick('tr')}>Team Room<span className="pod-tab-badge">2</span></button>
                                    <button className={`pod-tab ${activeTab === 'files' ? 'on' : ''}`} onClick={() => handleTabClick('fi')}>Files</button>
                                </div>

                                <div className="pod-pan on">
                                    {children}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ProblemStatementModal
                isOpen={isProblemModalOpen}
                onClose={() => setIsProblemModalOpen(false)}
                statement={podData?.problemStatement || ''}
            />
        </div>
    );
};
